import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import { ArrowLeft, Heart, Trash2, Loader2, LogOut, MessageCircle, ExternalLink, Package, CheckSquare, Square, Minus, Plus, ShoppingCart, FileDown, Landmark, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadPdf } from '../lib/download';

const API = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? `${API}/api/sourcing/img?u=${encodeURIComponent(u)}` : '');

const platformColor = (p) => ({
  '1688': 'bg-orange-100 text-orange-700 border-orange-200', Taobao: 'bg-red-100 text-red-700 border-red-200',
  Tmall: 'bg-rose-100 text-rose-700 border-rose-200', Alibaba: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Pinduoduo: 'bg-red-100 text-red-700 border-red-200',
}[p] || 'bg-gray-100 text-gray-700 border-gray-200');

function WishlistItem({ item, checked, onToggle, onQty, onRemove }) {
  const skus = (item.sku_selections || []).filter((s) => !['Item ID', 'SKU ID'].includes(s.name));
  return (
    <div className={`bg-white rounded-xl border p-4 flex gap-4 transition-shadow ${checked ? 'border-orange-400 shadow-md' : 'border-gray-200'}`} data-testid={`wishlist-item-${item.id}`}>
      <button onClick={onToggle} className="text-orange-600 flex-shrink-0 mt-1" data-testid={`wishlist-select-${item.id}`}>
        {checked ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-gray-300" />}
      </button>
      <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
        {item.image ? <img src={imgSrc(item.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-gray-300" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded border ${platformColor(item.platform)}`}>{item.platform}</span>
          {item.item_id && <span className="text-[10px] font-mono text-gray-400">#{item.item_id}</span>}
        </div>
        <h3 className="font-semibold text-gray-900 text-sm mt-1 leading-snug" data-testid={`wishlist-title-${item.id}`}>{item.product_title}</h3>
        {item.title_zh && item.title_zh !== item.product_title && <p className="text-xs text-gray-400 truncate">{item.title_zh}</p>}
        {skus.length > 0 && <p className="text-xs text-gray-500 mt-1">{skus.map((s) => `${s.name}: ${s.value}`).join(' · ')}</p>}
        <div className="flex flex-wrap items-center gap-4 mt-2">
          <span className="text-orange-600 font-bold">{item.price_rmb > 0 ? `¥${item.price_rmb.toFixed(2)}` : 'Price TBC'}<span className="text-xs text-gray-400 font-normal"> /unit</span></span>
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => onQty(Math.max(1, item.quantity - 1))} className="px-2 py-1 bg-gray-50 hover:bg-gray-100 text-gray-600"><Minus className="w-3.5 h-3.5" /></button>
            <span className="w-10 text-center text-sm font-semibold" data-testid={`wishlist-qty-${item.id}`}>{item.quantity}</span>
            <button onClick={() => onQty(item.quantity + 1)} className="px-2 py-1 bg-gray-50 hover:bg-gray-100 text-gray-600"><Plus className="w-3.5 h-3.5" /></button>
          </div>
          {item.price_rmb > 0 && <span className="text-xs text-gray-500">Subtotal ¥{(item.price_rmb * item.quantity).toFixed(2)}</span>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <button onClick={onRemove} className="text-gray-400 hover:text-red-600 p-1" data-testid={`wishlist-remove-${item.id}`}><Trash2 className="w-4 h-4" /></button>
        <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 p-1"><ExternalLink className="w-4 h-4" /></a>
      </div>
    </div>
  );
}

export default function ClientWishlist() {
  const { client, loading: authLoading, authHeaders, logout } = useClientAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/wishlist/my`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) { setItems(data.data); setSelected(new Set(data.data.map((i) => i.id))); }
    } catch { toast.error('Failed to load wishlist'); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!client) { navigate('/sourcing/auth'); return; }
    load();
  }, [client, authLoading, navigate, load]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.id)));

  const updateQty = async (id, quantity) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity } : i)));
    await fetch(`${API}/api/wishlist/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ quantity }) });
  };

  const remove = async (id) => {
    const res = await fetch(`${API}/api/wishlist/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { setItems((prev) => prev.filter((i) => i.id !== id)); setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; }); toast.success('Removed from wishlist'); }
  };

  const checkout = async () => {
    if (selected.size === 0) { toast.error('Select at least one item'); return; }
    setSubmitting(true);
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`${API}/api/orders/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ item_ids: [...selected], destination, notes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      setPlaced(data.data);
      setItems((prev) => prev.filter((i) => !selected.has(i.id)));
      setSelected(new Set());
      if (win) win.location.href = data.data.whatsapp_url; else window.location.href = data.data.whatsapp_url;
      toast.success(`${data.data.orders.length} order${data.data.orders.length === 1 ? '' : 's'} placed — ${data.data.reference}`);
    } catch (err) { if (win) win.close(); toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const downloadProforma = async () => {
    setDownloading(true);
    await downloadPdf(`${API}${placed.proforma_path}`, authHeaders(), `Proforma-${placed.reference}.pdf`);
    setDownloading(false);
  };

  const selectedItems = items.filter((i) => selected.has(i.id));
  const selectedTotal = selectedItems.reduce((s, i) => s + (i.price_rmb || 0) * i.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-wishlist-page">
      <nav className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/sourcing" className="flex items-center gap-2 text-blue-900 font-bold text-sm whitespace-nowrap" data-testid="wishlist-back-link"><ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Sourcing </span>Portal</Link>
          <div className="flex items-center gap-3 sm:gap-4 text-sm whitespace-nowrap">
            <Link to="/sourcing/orders" className="text-gray-600 hover:text-blue-900 flex items-center gap-1" data-testid="wishlist-nav-orders"><Package className="w-4 h-4" /> <span className="hidden sm:inline">My </span>Orders</Link>
            <span className="text-gray-600 hidden sm:inline">Hi, <strong>{client?.name}</strong></span>
            <button onClick={() => { logout(); navigate('/sourcing'); }} className="text-red-500 hover:text-red-700 flex items-center gap-1" aria-label="Logout" data-testid="wishlist-logout-btn"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        <div>
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="wishlist-heading"><Heart className="w-6 h-6 text-orange-600 fill-orange-600" /> Saved for Later</h1>
            {items.length > 0 && <button onClick={toggleAll} className="text-sm text-orange-600 hover:underline font-medium" data-testid="wishlist-select-all">{selected.size === items.length ? 'Deselect all' : 'Select all'}</button>}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300" data-testid="wishlist-empty">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nothing saved yet. Search a product link and tap "Save for Later".</p>
              <Link to="/sourcing" className="text-orange-600 hover:text-orange-700 font-medium text-sm mt-2 inline-block">Go to Sourcing Portal</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <WishlistItem key={item.id} item={item} checked={selected.has(item.id)} onToggle={() => toggle(item.id)}
                  onQty={(q) => updateQty(item.id, q)} onRemove={() => remove(item.id)} />
              ))}
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-20 self-start">
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="checkout-panel">
            <h2 className="font-bold text-gray-900 text-base mb-1 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-orange-600" /> Checkout</h2>
            <p className="text-xs text-gray-500 mb-4">Place one order for every selected item, send it to your agent and get a single proforma invoice for all of them.</p>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between text-gray-600"><span>Selected items</span><span className="font-semibold" data-testid="bulk-selected-count">{selected.size}</span></div>
              <div className="flex justify-between text-gray-600"><span>Total units</span><span className="font-semibold">{selectedItems.reduce((s, i) => s + i.quantity, 0)}</span></div>
              <div className="flex justify-between text-gray-900 font-bold border-t border-gray-100 pt-2"><span>Goods total</span><span className="text-orange-600" data-testid="bulk-total">¥{selectedTotal.toFixed(2)}</span></div>
            </div>
            <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Destination (e.g. Accra, Ghana)"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm mb-2 focus:border-orange-500 focus:outline-none" data-testid="bulk-destination-input" />
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes for our agent (branding, packaging, deadline...)"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm mb-3 focus:border-orange-500 focus:outline-none" data-testid="bulk-notes-input" />
            <Button onClick={checkout} disabled={submitting || selected.size === 0} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl" data-testid="bulk-checkout-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />} Place Order & Send to Agent{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
            <p className="text-[11px] text-gray-400 mt-2 text-center">Pay afterwards via Stanbic Bank CIPS transfer — one reference for all items.</p>
            {placed && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm space-y-3" data-testid="checkout-success">
                <p className="font-bold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> {placed.orders.length} order{placed.orders.length === 1 ? '' : 's'} placed</p>
                <div className="text-xs text-emerald-900 space-y-0.5">
                  <p>Reference <span className="font-mono font-semibold" data-testid="checkout-reference">{placed.reference}</span> · Total <strong>¥{placed.total_rmb.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></p>
                  <p className="text-emerald-700">{placed.orders.map((o) => `#${o.id}`).join(', ')}</p>
                </div>
                <a href={placed.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl" data-testid="checkout-whatsapp-link"><MessageCircle className="w-4 h-4" /> Chat with Agent on WhatsApp</a>
                <Button type="button" variant="outline" onClick={downloadProforma} disabled={downloading} className="w-full border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold rounded-xl" data-testid="checkout-proforma-btn">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />} Download Proforma Invoice{placed.orders.length > 1 ? ' (all items)' : ''}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(placed.batch ? `/sourcing/orders?batch=${placed.batch.id}` : `/sourcing/orders?highlight=${placed.orders[0].id}`)} className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 font-semibold rounded-xl" data-testid="checkout-pay-btn">
                  <Landmark className="w-4 h-4 mr-2" /> Pay via Stanbic CIPS
                </Button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
